import { saveExtrasBeforeContinue } from './event-handlers.js';
import { getCurrentStageId, setCurrentStageId } from './state.js';
import { isStage1Valid, isStage2Valid, isStage3Valid, isStage4Valid, isXCoverStageValid } from './validation.js';
import { showNextStageUI, returnToStageUI } from './ui/stage-transition.js';
import { getVisibleStages, shouldAutoAdvanceStage, syncStageVisibility } from './stage-visibility.js';

// TODO: This should be temporary, but to hook into the stage complete handler, this is needed for now
import { dispatchStageCompleteEvent } from "./stage-complete-event.js";
import { onBeforeNextStageComplete } from './before-stage-complete.js';


// NOTE: the 4 main stages of checkout all require validation, but there are other dynamically added stages that may or may not
// TODO: make this dynamic, map validation functions to stages dynamically if the data indicates a stage needs validation
// TODO: also map stage completion handlers to stages dynamically - could have a file that dynamically defines validatorDispatch and stageCompleteDispatch
const validatorDispatch = {
    booking: isStage1Valid,
    extras:  isStage2Valid,
    party:   isStage3Valid,
    payment: isStage4Valid,
    xcover:  isXCoverStageValid
}


let savingExtras = false;
async function handleNextStage(stageId) {
    if (savingExtras) return;
    // if validation fails, immediately return, don't allow user to move to next stage
    syncStageVisibility();
    const nextStageDecision = getNextStageDecision(stageId);

    if (!nextStageDecision.isAllowed) {
        return;
    }

    if (stageId === 'extras') {
        savingExtras = true;
        const section = document.getElementById('atb-extras-section');
        let message = section.querySelector('[data-extras-save-error]');
        if (!message) {
            message = document.createElement('div');
            message.dataset.extrasSaveError = '';
            message.className = 'alert alert-danger';
            message.setAttribute('role', 'alert');
            section.prepend(message);
        }
        message.hidden = true;
        section.setAttribute('aria-busy', 'true');
        try {
            await saveExtrasBeforeContinue();
        } catch (error) {
            message.textContent = error.message || 'We could not save your extras. Please try Continue again.';
            message.hidden = false;
            return;
        } finally {
            section.removeAttribute('aria-busy');
            savingExtras = false;
        }
        // The guest may have returned to Booking while the request was pending.
        if (getCurrentStageId() !== stageId) return;
    }

    const nextStageId = await prepareNextVisibleStage(stageId, nextStageDecision.to);
    if (!nextStageId) {
        dispatchStageComplete(nextStageDecision.from, null);
        return;
    }

    setCurrentStageId(nextStageId);

    // Run Render function
    showNextStageUI(stageId, nextStageId);

    dispatchStageComplete(nextStageDecision.from, nextStageId);
}

async function prepareNextVisibleStage(currentStageId, nextStageId) {
    const preparedStageIds = new Set();
    let nextVisibleStageAnchor = currentStageId;

    while (nextStageId && !preparedStageIds.has(nextStageId)) {
        preparedStageIds.add(nextStageId);

        // Run logic before the next stage - i.e. getting prices for extras and attaching labels
        await onBeforeNextStageComplete(nextStageId);
        syncStageVisibility();

        const visibleNextStageId = getNextVisibleStageId(nextVisibleStageAnchor);
        if (visibleNextStageId !== nextStageId) {
            nextStageId = visibleNextStageId;
            continue;
        }

        if (shouldAutoAdvanceStage(nextStageId)) {
            nextVisibleStageAnchor = nextStageId;
            nextStageId = getNextVisibleStageId(nextStageId);
            continue;
        }

        break;
    }

    return nextStageId;
}

function handleReturnToStage(targetStageId) {
    const returnStageDecision = getReturnStageDecision(targetStageId);
    if (!returnStageDecision.isAllowed) {
        console.warn('Cannot return to target stage', targetStageId);
        return;
    }

    setCurrentStageId(returnStageDecision.to);

    // Run Render function
    returnToStageUI(returnStageDecision.to, returnStageDecision.from);    
}


/**
 * Determines whether transitioning to the next stage is allowed and
 * provides metadata about the current and next stages.
 *
 * @param {string|number} stageId - The ID of the current stage.
 * @returns {Object} decision
 * @returns {boolean} decision.isAllowed - Whether moving to the next stage is permitted.
 * @returns {string|number} decision.from - The ID of the current stage.
 * @returns {string|number|undefined} decision.to - The ID of the next stage, if one exists.
 * @returns {Object} decision.current - The current stage object.
 * 
 */
function getNextStageDecision(stageId) {
    const stages            = getVisibleStages();
    const currentStageIndex = getStageIndexById(stageId, stages);
    const currentStage      = stages[currentStageIndex];
    const hasNextStage      = currentStageIndex + 1 < stages.length;
    const currentStageValid = isStageValid(stageId);

    return {
        isAllowed: currentStageValid,
        from     : currentStage.id,
        to       : stages[currentStageIndex + 1]?.id,
        current  : currentStage
    }
}


function getReturnStageDecision(targetStageId) {
    const stages = getVisibleStages();
    const currentStageIndex = getStageIndexById(getCurrentStageId(), stages);
    const currentStage = stages[currentStageIndex];

    const returnStage = stages.find(stage => stage.id === targetStageId);
    if (!returnStage) return { allowed: false };

    return {
        isAllowed: true,
        from: currentStage.id,
        to: returnStage.id,
        current: currentStage
    }
}


function isStageValid(stageId) {
    console.log('running stage validation');
    const stage = getVisibleStages().find(stage => stage.id == stageId);
    
    // if the stage does not require validation, 
    if (!stage.validate)
        return true;

    // if for whatever reason, the stage requires validation but 
    if (!validatorDispatch[stageId]) {
        console.error(`Validation required but no validator found for Stage: ${stageId}`);
        return false;
    }

    // run validation
    return validatorDispatch[stageId]();
}


function getStageIndexById(stageId, stages) {
    let currentStageIndex = 0;
    stages.forEach((stage, index) => {
        if (stage.id !== stageId)
            return;

        currentStageIndex = index;
    });
    
    return currentStageIndex;
}

function getNextVisibleStageId(stageId) {
    const stages = getVisibleStages();
    const currentStageIndex = getStageIndexById(stageId, stages);

    return stages[currentStageIndex + 1]?.id;
}


function dispatchStageComplete(stageId, nextStageId) {
    dispatchStageCompleteEvent(stageId, nextStageId);
}

export { handleNextStage, handleReturnToStage };
